import { IPartCategorySubPartLink } from "../models/PartCategorySubPart";

/** Main category id -> its sub-part category ids. */
export function subPartIdsByMainId(links: IPartCategorySubPartLink[]): Map<string, string[]> {
  const byMain = new Map<string, string[]>();
  links.forEach(({ mainCategoryId, subPartCategoryId }) => {
    const existing = byMain.get(mainCategoryId);
    if (existing) existing.push(subPartCategoryId);
    else byMain.set(mainCategoryId, [subPartCategoryId]);
  });
  return byMain;
}

/**
 * The sub-parts win only when together they're worth strictly more than the main. A tie -
 * including neither side having a price - keeps the main: one part to take off and sell.
 */
export const subPartsOutvalueMain = (mainPrice: number, subPartPrices: number[]): boolean =>
  subPartPrices.reduce((sum, price) => sum + price, 0) > mainPrice;

/**
 * Category ids to leave out so a main category and its sub-parts are never both taken. For each
 * main, whichever side is worth more wins: an engine at £100 whose sub-parts come to £150 is
 * taken as the sub-parts.
 *
 * - `priceByCategoryId` holds only the categories that could be taken. A main missing from it
 *   can't win, so it's skipped; a sub-part missing from it counts as £0.
 * - `takenCategoryIds` are categories already dealt with (parted out, or sold), mains and
 *   sub-parts alike. That decision was made at the time, so it's never re-run: a taken main took
 *   its sub-parts with it, so those are always left out; a taken sub-part means the main is no
 *   longer whole, so the main is always left out (its other sub-parts stay free to take).
 */
export function mainVsSubPartsLosers(
  links: IPartCategorySubPartLink[],
  priceByCategoryId: Map<string, number>,
  takenCategoryIds: Set<string> = new Set()
): Set<string> {
  const losers = new Set<string>();

  subPartIdsByMainId(links).forEach((subPartIds, mainId) => {
    if (takenCategoryIds.has(mainId)) {
      subPartIds.forEach((id) => losers.add(id));
      return;
    }

    // Must come before the price comparison: a taken sub-part is absent from the price map, so
    // it would count as £0 and hand the main a win it can no longer have.
    if (subPartIds.some((id) => takenCategoryIds.has(id))) {
      losers.add(mainId);
      return;
    }

    if (!priceByCategoryId.has(mainId)) return;

    const mainPrice = priceByCategoryId.get(mainId) ?? 0;
    const subPartPrices = subPartIds.map((id) => priceByCategoryId.get(id) ?? 0);

    if (subPartsOutvalueMain(mainPrice, subPartPrices)) losers.add(mainId);
    else subPartIds.forEach((id) => losers.add(id));
  });

  return losers;
}
